import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Download, Users, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const COLUMNS = [
  ['Full Name', (u) => u.full_name || '—', 'min-w-[160px] font-bold'],
  ['Mobile', (u) => u.mobile_number || '—', 'min-w-[140px] whitespace-nowrap'],
  ['Email', (u) => u.email || '—', 'min-w-[200px] break-all'],
  ['Residential Address', (u) => u.residential_address || '—', 'min-w-[260px] max-w-[320px]'],
  ['Consent', (u) => (u.notification_consent ? 'YES' : 'NO'), 'min-w-[80px]'],
  ['Consent At', (u) => fmt(u.notification_consent_at), 'min-w-[160px] whitespace-nowrap'],
  ['Registered On', (u) => fmt(u.created_at), 'min-w-[160px] whitespace-nowrap'],
  ['Last Updated', (u) => fmt(u.updated_at), 'min-w-[160px] whitespace-nowrap'],
  ['Bookings', (u) => u.booking_count, 'min-w-[80px] text-center'],
  ['Aadhaar (legacy)', (u) => u.aadhaar_masked || '—', 'min-w-[140px] whitespace-nowrap'],
];

export const RegisteredUsersTab = () => {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/users`, { params: q ? { q } : {} });
      setUsers(res.data.users || []);
      setTotal(res.data.total_registered || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const onSearch = (e) => { e.preventDefault(); load(query.trim()); };
  const clear = () => { setQuery(''); load(''); };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/users/export.xlsx`, {
        params: query.trim() ? { q: query.trim() } : {}, responseType: 'blob',
      });
      const m = /filename="?([^";]+)"?/i.exec(res.headers['content-disposition'] || '');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'LFJ_Registered_Users.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setError('Download failed — please try again');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="registered-users-tab" className="card-brutalist bg-white p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-black font-mono uppercase flex items-center gap-2"><Users size={18} />Registered Users</h2>
          <p className="text-xs font-mono text-gray-600 mt-1" data-testid="registered-users-count">
            Showing {users.length} of {total} registered users
          </p>
        </div>
        <button
          data-testid="registered-users-download-btn"
          onClick={download}
          disabled={downloading}
          className="px-4 py-2 border-2 border-black bg-black text-white font-mono uppercase text-xs font-bold flex items-center gap-2 hover:bg-gray-800 disabled:opacity-50"
        >
          <Download size={14} />{downloading ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="registered-users-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by mobile number, name or email"
            style={{ paddingLeft: '2.25rem' }}
            className="w-full pr-3 py-2 border-2 border-black font-mono text-sm"
          />
        </div>
        <button data-testid="registered-users-search-btn" type="submit" className="px-4 py-2 border-2 border-black bg-[#FF4500] font-mono uppercase text-xs font-bold">Search</button>
        {query && (
          <button data-testid="registered-users-clear-btn" type="button" onClick={clear} className="px-3 py-2 border-2 border-black bg-white font-mono uppercase text-xs flex items-center gap-1"><X size={12} />Clear</button>
        )}
      </form>

      {error && <p data-testid="registered-users-error" className="text-red-600 text-sm font-mono mb-3">{error}</p>}

      <div className="overflow-x-auto border-2 border-black">
        <table className="w-full text-sm" data-testid="registered-users-table">
          <thead className="bg-gray-100">
            <tr>
              {COLUMNS.map(([h]) => (
                <th key={h} className="text-left p-3 font-mono uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={COLUMNS.length} className="p-6 text-center font-mono text-gray-500">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="p-6 text-center font-mono text-gray-500" data-testid="registered-users-empty">No users found</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id} data-testid={`registered-user-row-${u.user_id}`} className="border-t border-gray-200 align-top hover:bg-orange-50/40">
                {COLUMNS.map(([h, get, cls]) => (
                  <td key={h} className={`p-3 font-mono text-xs ${cls}`}>{get(u)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
